import React, {
  useEffect,
  useState,
} from "react";

import {
  Check,
  X,
} from "@phosphor-icons/react";
import { toast } from "sonner";


const UNITS = [
  {
    value: "piece",
    label: "Pièce",
  },
  {
    value: "meter",
    label: "Mètre",
  },
  {
    value: "liter",
    label: "Litre",
  },
  {
    value: "kilogram",
    label: "Kilogramme",
  },
  {
    value: "other",
    label: "Autre",
  },
];


const EMPTY_FORM = {
  reference: "",
  name: "",
  description: "",
  category: "",
  unit: "piece",
  supplier: "",
  unit_cost_ht: "",
  initial_quantity: "0",
  minimum_quantity: "0",
  location: "",
};


function initialForm(
  mode,
  part
) {
  if (
    mode !== "edit" ||
    !part
  ) {
    return EMPTY_FORM;
  }

  return {
    reference:
      part.reference || "",
    name:
      part.name || "",
    description:
      part.description || "",
    category:
      part.category || "",
    unit:
      part.unit || "piece",
    supplier:
      part.supplier || "",
    unit_cost_ht:
      part.unitCostHt ??
      "",
    initial_quantity: "0",
    minimum_quantity:
      part.minimumQuantity ??
      0,
    location:
      part.location || "",
  };
}


export default function PartFormModal({
  open,
  mode = "create",
  part = null,
  saving = false,
  onClose,
  onSubmit,
}) {
  const [form, setForm] =
    useState(EMPTY_FORM);


  useEffect(() => {
    if (!open) {
      return;
    }

    setForm(
      initialForm(
        mode,
        part
      )
    );
  }, [
    open,
    mode,
    part,
  ]);


  if (!open) {
    return null;
  }


  const updateField = (
    field,
    value
  ) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  };


  const submit = (
    event
  ) => {
    event.preventDefault();

    const reference =
      form.reference.trim();

    const name =
      form.name.trim();

    if (!reference) {
      toast.error(
        "La référence est obligatoire."
      );

      return;
    }

    if (!name) {
      toast.error(
        "Le nom est obligatoire."
      );

      return;
    }

    const minimumQuantity =
      Number(
        form.minimum_quantity
      );

    if (
      Number.isNaN(
        minimumQuantity
      ) ||
      minimumQuantity < 0
    ) {
      toast.error(
        "Le seuil minimum est invalide."
      );

      return;
    }

    const unitCost =
      form.unit_cost_ht === ""
        ? null
        : Number(
            form.unit_cost_ht
          );

    if (
      unitCost !== null &&
      (
        Number.isNaN(unitCost) ||
        unitCost < 0
      )
    ) {
      toast.error(
        "Le prix d’achat est invalide."
      );

      return;
    }

    const payload = {
      reference,
      name,
      description:
        form.description.trim(),
      category:
        form.category.trim(),
      unit: form.unit,
      supplier:
        form.supplier.trim(),
      unit_cost_ht:
        unitCost,
      minimum_quantity:
        minimumQuantity,
      location:
        form.location.trim(),
    };

    if (mode === "create") {
      const initialQuantity =
        Number(
          form.initial_quantity
        );

      if (
        Number.isNaN(
          initialQuantity
        ) ||
        initialQuantity < 0
      ) {
        toast.error(
          "Le stock initial est invalide."
        );

        return;
      }

      payload.initial_quantity =
        initialQuantity;
    }

    onSubmit(payload);
  };


  const inputClass =
    "w-full rounded-lg border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2 text-sm outline-none transition focus:border-[var(--do-accent)] disabled:cursor-not-allowed disabled:opacity-60";


  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center acta-modal-overlay p-3 backdrop-blur-sm sm:p-6"
      data-testid="part-form-modal"
      onMouseDown={onClose}
    >
      <div
        className="acta-modal-surface max-h-[94vh] w-full max-w-3xl overflow-y-auto rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] shadow-2xl"
        onMouseDown={(event) =>
          event.stopPropagation()
        }
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 py-4 sm:px-6">
          <div>
            <div className="label-eyebrow">
              Stock GMAO
            </div>

            <h2 className="mt-1 font-heading text-xl font-black">
              {mode === "create"
                ? "Nouvelle pièce"
                : "Modifier la pièce"}
            </h2>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            aria-label="Fermer"
            data-testid="part-form-close"
            className="rounded-lg p-2 hover:bg-[var(--do-surface-soft)] disabled:opacity-50"
          >
            <X size={20} />
          </button>
        </div>

        <form
          onSubmit={submit}
          className="space-y-5 p-4 sm:p-6"
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label
                htmlFor="part-reference"
                className="label-eyebrow mb-1.5 block"
              >
                Référence *
              </label>

              <input
                id="part-reference"
                type="text"
                value={form.reference}
                onChange={(event) =>
                  updateField(
                    "reference",
                    event.target.value
                  )
                }
                disabled={saving}
                maxLength={100}
                data-testid="part-reference-input"
                className={inputClass}
              />
            </div>

            <div>
              <label
                htmlFor="part-name"
                className="label-eyebrow mb-1.5 block"
              >
                Nom *
              </label>

              <input
                id="part-name"
                type="text"
                value={form.name}
                onChange={(event) =>
                  updateField(
                    "name",
                    event.target.value
                  )
                }
                disabled={saving}
                maxLength={200}
                data-testid="part-name-input"
                className={inputClass}
              />
            </div>
          </div>

          <div>
            <label
              htmlFor="part-description"
              className="label-eyebrow mb-1.5 block"
            >
              Description
            </label>

            <textarea
              id="part-description"
              value={form.description}
              onChange={(event) =>
                updateField(
                  "description",
                  event.target.value
                )
              }
              disabled={saving}
              rows={4}
              maxLength={4000}
              data-testid="part-description-input"
              className={`${inputClass} resize-y`}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label
                htmlFor="part-category"
                className="label-eyebrow mb-1.5 block"
              >
                Catégorie
              </label>

              <input
                id="part-category"
                type="text"
                value={form.category}
                onChange={(event) =>
                  updateField(
                    "category",
                    event.target.value
                  )
                }
                disabled={saving}
                maxLength={100}
                placeholder="Mécanique, électrique…"
                data-testid="part-category-input"
                className={inputClass}
              />
            </div>

            <div>
              <label
                htmlFor="part-unit"
                className="label-eyebrow mb-1.5 block"
              >
                Unité
              </label>

              <select
                id="part-unit"
                value={form.unit}
                onChange={(event) =>
                  updateField(
                    "unit",
                    event.target.value
                  )
                }
                disabled={saving}
                data-testid="part-unit-select"
                className={inputClass}
              >
                {UNITS.map(
                  (unit) => (
                    <option
                      key={unit.value}
                      value={unit.value}
                    >
                      {unit.label}
                    </option>
                  )
                )}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label
                htmlFor="part-supplier"
                className="label-eyebrow mb-1.5 block"
              >
                Fournisseur
              </label>

              <input
                id="part-supplier"
                type="text"
                value={form.supplier}
                onChange={(event) =>
                  updateField(
                    "supplier",
                    event.target.value
                  )
                }
                disabled={saving}
                maxLength={200}
                data-testid="part-supplier-input"
                className={inputClass}
              />
            </div>

            <div>
              <label
                htmlFor="part-location"
                className="label-eyebrow mb-1.5 block"
              >
                Emplacement
              </label>

              <input
                id="part-location"
                type="text"
                value={form.location}
                onChange={(event) =>
                  updateField(
                    "location",
                    event.target.value
                  )
                }
                disabled={saving}
                maxLength={200}
                placeholder="Dépôt A — Étagère 3"
                data-testid="part-location-input"
                className={inputClass}
              />
            </div>
          </div>

          <div className={`grid grid-cols-1 gap-4 ${
            mode === "create"
              ? "sm:grid-cols-3"
              : "sm:grid-cols-2"
          }`}>
            {mode === "create" && (
              <div>
                <label
                  htmlFor="part-initial-quantity"
                  className="label-eyebrow mb-1.5 block"
                >
                  Stock initial
                </label>

                <input
                  id="part-initial-quantity"
                  type="number"
                  min="0"
                  step="0.01"
                  value={
                    form.initial_quantity
                  }
                  onChange={(event) =>
                    updateField(
                      "initial_quantity",
                      event.target.value
                    )
                  }
                  disabled={saving}
                  data-testid="part-initial-quantity-input"
                  className={inputClass}
                />
              </div>
            )}

            <div>
              <label
                htmlFor="part-minimum-quantity"
                className="label-eyebrow mb-1.5 block"
              >
                Seuil minimum
              </label>

              <input
                id="part-minimum-quantity"
                type="number"
                min="0"
                step="0.01"
                value={
                  form.minimum_quantity
                }
                onChange={(event) =>
                  updateField(
                    "minimum_quantity",
                    event.target.value
                  )
                }
                disabled={saving}
                data-testid="part-minimum-quantity-input"
                className={inputClass}
              />
            </div>

            <div>
              <label
                htmlFor="part-unit-cost"
                className="label-eyebrow mb-1.5 block"
              >
                Prix d’achat HT
              </label>

              <input
                id="part-unit-cost"
                type="number"
                min="0"
                step="0.01"
                value={
                  form.unit_cost_ht
                }
                onChange={(event) =>
                  updateField(
                    "unit_cost_ht",
                    event.target.value
                  )
                }
                disabled={saving}
                data-testid="part-unit-cost-input"
                className={inputClass}
              />
            </div>
          </div>

          <div className="flex flex-col-reverse gap-2 border-t border-[var(--do-border)] pt-4 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="rounded-lg border border-[var(--do-border)] px-4 py-2 text-sm font-heading font-semibold disabled:opacity-50"
            >
              Annuler
            </button>

            <button
              type="submit"
              disabled={saving}
              data-testid="part-form-submit"
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-[var(--do-accent)] px-4 py-2 text-sm font-heading font-bold text-[var(--do-on-accent)] disabled:opacity-50"
            >
              <Check
                size={17}
                weight="bold"
              />

              {saving
                ? "Enregistrement…"
                : mode === "create"
                  ? "Créer la pièce"
                  : "Enregistrer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
